import $ from 'jquery';
import * as React from 'react';
import { Helmet } from 'react-helmet';

import Loader from 'components/atoms/Loader';
import Ribbon from 'components/atoms/Ribbon';
import { getUIAssetPath } from 'components/atoms/UIAsset';
import ErrorBoundary from 'components/helper/ErrorBoundary/ErrorBoundary';
import GTM from 'components/helper/GTM/GTM';
import { PlatformDebugger } from 'components/helper/Platform';
import { WebPlatformComponent } from 'components/helper/Platform';
import AppStoreOverlay from 'components/molecules/Notification/static/AppStore';
import ConnectionHandler from 'containers/helper/Connection/Handler';
import EventsController from 'containers/molecules/EventsController/EventsController';
import OffCanvas from 'containers/molecules/OffCanvas';
import Header from 'containers/organisms/Header';
import Layout from 'containers/organisms/Layout';
import { GTM_ID } from 'defs/ga/config';
import routesConfig, { getBaseTitle, getSeoDataFromViewId } from 'defs/routes';
import { getDevicePlatform } from 'helper/device';
import { DATA_LAYER_REF } from 'helper/ga';
import i18n from 'helper/i18n';
import historyMiddleware from 'helper/middleware/history';
import { cleanTemplate, compileDynamicRoute } from 'helper/string';
import { reload } from 'lib/ApplicationLoader';
import { delayed } from 'lib/Job';
import { register as registerSharedEvents } from 'lib/SharedEvents';
import { AppProps } from 'types/App';

class App extends React.Component<AppProps> {
	public static displayName = 'App';

	get historyMiddleware() {
		return historyMiddleware;
	}

	get $body() {
		return $('body');
	}

	public shouldRedirectToFirstStep(nextProps: AppProps): boolean {
		const { location } = this.props;
		const from: string = location.pathname;
		const to: string = nextProps.location.pathname;
		const isFromAnamnesisDone: boolean = location.search.search('anamnesisDone=true') >= 0;

		return (
			isFromAnamnesisDone &&
			to === routesConfig.anamnesis.disclaimer.href &&
			from !== routesConfig.anamnesis.verify.href
		);
	}

	public checkHeaderFooterInvisible(props: AppProps) {
		if (props.matchingRouteConfig.headerFooterInvisible === true) {
			this.$body.addClass('state-o-app--header-footer-invisible');
			return;
		}

		this.$body.removeClass('state-o-app--header-footer-invisible');
	}

	public whenUpdated(prevProps: AppProps) {
		const { isOnline, location, history, store } = prevProps;
		const navigated = this.props.location !== location;

		if (this.props.isOnline && !isOnline) {
			return reload();
		}

		if (!navigated) {
			return;
		}

		this.checkHeaderFooterInvisible(this.props);

		const from = location.pathname;
		const to = this.props.location.pathname;

		if (from === to) {
			return;
		}

		const anamnesisLeaveAllowed = this.historyMiddleware(store, from, to);

		// TBD
		if (anamnesisLeaveAllowed && this.shouldRedirectToFirstStep(this.props)) {
			const atidpublic = delayed(() => {
				history.replace(routesConfig.anamnesis.selectCoach.href);
				window.clearTimeout(atidpublic);
			}, 100);
			return;
		}

		if (anamnesisLeaveAllowed) {
			return;
		}

		const tid = delayed(() => {
			history.replace(location.pathname);
			window.clearTimeout(tid);
		}, 100);
	}

	public checkRouteConditions() {
		const {
			history,

			isAuthenticated,
			hasAuthenticationLoaded,

			isProfileLoaded,
			isAnamneseDone,
			doSaveRedirectRoutePath,
			matchingRouteConfig,
			match,
		}: AppProps = this.props;

		if (matchingRouteConfig.requireAuth && isAuthenticated === false && hasAuthenticationLoaded) {
			doSaveRedirectRoutePath(compileDynamicRoute(match.path, match.params));
			history.push(routesConfig.login.indexRoute.href);
			return;
		}

		if (matchingRouteConfig.requireAnamnese && isProfileLoaded && !isAnamneseDone) {
			history.push(routesConfig.anamnesis.selectCoach.href);
			return;
		}

		if (match.path === routesConfig.login.indexRoute.href && isAuthenticated && isProfileLoaded) {
			history.push(routesConfig.dashboard.indexRoute.href);
			return;
		}
	}

	public checkLoadUserData() {
		const { isAuthenticated, doLoadUserData, isLoadingProfile, isProfileLoaded }: AppProps = this.props;

		if (isAuthenticated && !isLoadingProfile && !isProfileLoaded) {
			doLoadUserData();
		}
	}

	public componentDidMount(): void {
		const {
			matchingRouteConfig,
			doChangeViewID,
			doStoreRouterHistory,
			doRequestCustomOverlays,
			history,
		} = this.props;

		this.checkHeaderFooterInvisible(this.props);

		registerSharedEvents();

		this.checkRouteConditions();
		this.checkLoadUserData();

		doChangeViewID(matchingRouteConfig.id);
		doStoreRouterHistory(history);
		doRequestCustomOverlays();

		this.setReactAppLoaded();
	}

	public scrollRestoration(prevProps: AppProps) {
		if (this.props.location !== prevProps.location && this.props.matchingRouteConfig.scrollTop === true) {
			window.scrollTo(0, 0);
		}
	}

	public componentDidUpdate(prevProps: AppProps): void {
		this.whenUpdated(prevProps);

		const { matchingRouteConfig, doChangeViewID } = this.props;

		this.scrollRestoration(prevProps);

		if (matchingRouteConfig.id !== prevProps.matchingRouteConfig.id) {
			this.resetScrollPosition();
		}

		this.checkRouteConditions();
		this.checkLoadUserData();

		doChangeViewID(matchingRouteConfig.id);
	}

	public setReactAppLoaded() {
		this.$body.removeClass('state-o-app--loading');
	}

	public resetScrollPosition(): void {
		const elements = [
			document.body ? document.body : {},
			document.documentElement ? document.documentElement : {},
		].filter(Boolean);

		elements.map((element: any) => {
			try {
				element.scrollTop = 0;
				return true;
			} catch (err) {
				/* do nothing */
			}

			return false;
		});
	}

	public handleGoBack = () => {
		const { history, match } = this.props;
		const from: string = match.path;

		if (from.search(routesConfig.favorites.indexRoute.href) >= 0) {
			return history.push(routesConfig.profile.indexRoute.href);
		}

		if (from.search(routesConfig.program.further.href) >= 0) {
			return history.push(routesConfig.dashboard.indexRoute.href);
		}

		if (from.search(routesConfig.program.recommended.href) >= 0) {
			return history.push(routesConfig.dashboard.indexRoute.href);
		}

		if (from.search(routesConfig.program.indexRoute.href) >= 0) {
			if (from.replace(routesConfig.program.indexRoute.href, '').length > 0) {
				return history.push(routesConfig.program.recommended.href);
			}

			return history.push(routesConfig.dashboard.indexRoute.href);
		}

		history.push(routesConfig.dashboard.indexRoute.href);
	};

	get initialHeaderData(): ReturnType<typeof getSeoDataFromViewId> {
		const { currentViewID } = this.props;
		return getSeoDataFromViewId(currentViewID);
	}

	public renderChildren() {
		return React.Children.map(this.props.children as any[], (child: any) => {
			return React.cloneElement(child, {
				matchingRouteConfig: this.props.matchingRouteConfig,
			});
		});
	}

	public render() {
		const {
			isAuthenticated,
			isLoadingProfile,
			isOnline,
			matchingRouteConfig,
			doOpenOffCanvas,
			doCloseOffCanvas,
			match,
		} = this.props;

		const isAwaitingAuthentication = matchingRouteConfig.requireAuth ? !isAuthenticated : false;
		const isLoading = isAwaitingAuthentication || isLoadingProfile;
		/* || (matchingRouteConfig.requireAnamnese && !isProfileLoaded); */

		const seoData = this.initialHeaderData;
		const classList = ['o-app__inner'];

		if (!isOnline) {
			classList.push('state-o-app__inner--offline');
		}

		return (
			<ErrorBoundary>
				<div className={classList.join(' ').trimRight()} data-platform={`${getDevicePlatform().toLowerCase()}`}>
					<Layout />
					{!isOnline && <Ribbon text={i18n('react.impulsCoach.components.ribbon.offline.text')} />}
					<ConnectionHandler />
					<PlatformDebugger />
					{seoData.title && (
						<Helmet>
							<title>
								{seoData.title.length > 0
									? `${cleanTemplate(`${seoData.title} ${getBaseTitle()}`)}`
									: i18n('react.impulsCoach.routes.title')}
							</title>
							{seoData.meta && seoData.meta.description && (
								<meta name="description" content={`${seoData.meta.description}`} />
							)}
							{seoData.og && seoData.og.title && (
								<meta property="og:title" content={`${seoData.og.title}`} />
							)}
							<meta property="og:image" content={getUIAssetPath('/img/svg/logo_coach.svg')} />
						</Helmet>
					)}
					<GTM id={GTM_ID} dataLayerRef={DATA_LAYER_REF} />
					<WebPlatformComponent>
						<AppStoreOverlay />
					</WebPlatformComponent>
					<Header
						onBack={this.handleGoBack}
						onTrigger={doOpenOffCanvas}
						dayNumber={parseInt(`${match.params.day}`, 10) || null}
					/>
					<EventsController />
					<OffCanvas onClose={doCloseOffCanvas} />
					{isLoading && <Loader />}
					{!isLoading && this.renderChildren()}
				</div>
			</ErrorBoundary>
		);
	}
}

export default App;
